import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ErrorDialog from './ErrorDialog';
import SuccessDialog from './SuccessDialog';
import BlueButton from '../BlueButton';
import PinkGradientButton from '../PinkGradientButton';
import { notifyAuthExpired, isAuthExpiredError, SESSION_REFRESH_MESSAGE, SESSION_REFRESH_TITLE } from '../../../utils/auth-error-utils';

const ApiButton = ({
    apiCall,
    onSuccess,
    onError,
    buttonLabel,
    buttonStyle,
    variant = 'contained',
    color = 'secondary',
    errorTitle,
    errorMessage,
    successTitle,
    successMessage,
    disabled = false,
    onErrorConfirm = () => {},
    onSuccessConfirm = () => {},
    fullWidth = false,
    sx = {},
    image,
    imageSize,
    buttonVariant = 'blue',
    showSuccessDialog = true,
}) => {
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState('An error occurred.');
    const [errorTitleOverride, setErrorTitleOverride] = useState(null);
    const [showReloadAction, setShowReloadAction] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const defaultErrorHandler = (error) => {
        const isAuthExpired = isAuthExpiredError(error);
        if (isAuthExpired) {
            notifyAuthExpired({ source: 'api-button' });
            return;
        }
        setErrorDialogMessage(isAuthExpired ? SESSION_REFRESH_MESSAGE : errorMessage || error.message || 'An error occurred.');
        setErrorTitleOverride(isAuthExpired ? SESSION_REFRESH_TITLE : null);
        setShowReloadAction(isAuthExpired);
        setOpenErrorDialog(true);
    };

    const defaultSuccessHandler = () => {
        if (showSuccessDialog) {
            setOpenSuccessDialog(true);
        }
    };

    const handleClick = async () => {
        setIsLoading(true);
        try {
            console.warn('Operation started...');
            const result = await apiCall();
            console.warn('Operation completed successfully!');
            if (onSuccess) {
                onSuccess(result);
            } else {
                defaultSuccessHandler();
            }
        } catch (error) {
            console.error('Operation failed:', error);
            if (onError) {
                onError(error);
            } else {
                defaultErrorHandler(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const renderButton = () => {
        const commonProps = {
            onClick: handleClick,
            disabled: isLoading || disabled,
            sx: {
                ...sx,
                ...buttonStyle,
                ...(isLoading || disabled
                    ? { backgroundColor: '#F0EFF1!important', cursor: 'not-allowed!important', pointerEvents: 'none' }
                    : {}),
            },
        };

        switch (buttonVariant) {
            case 'pink':
                return (
                    <PinkGradientButton {...commonProps}>
                        {isLoading ? <CircularProgress size={24} /> : buttonLabel}
                        {image && imageSize && <img src={image} width={imageSize} style={{ marginLeft: '8px' }} />}
                    </PinkGradientButton>
                );
            case 'blue':
            default:
                return (
                    <BlueButton {...commonProps}>
                        {isLoading ? <CircularProgress size={24} /> : buttonLabel}
                        {image && imageSize && <img src={image} width={imageSize} style={{ marginLeft: '8px' }} />}
                    </BlueButton>
                );
        }
    };

    return (
        <>
            <Box sx={sx}>{renderButton()}</Box>

            <ErrorDialog
                open={openErrorDialog}
                handleClose={() => {
                    setOpenErrorDialog(false);
                    setErrorTitleOverride(null);
                    setShowReloadAction(false);
                    onErrorConfirm();
                }}
                title={errorTitleOverride || errorTitle || 'Error'}
                message={errorDialogMessage}
                primaryActionText={showReloadAction ? 'Reload Page' : null}
                onPrimaryAction={showReloadAction ? () => window.location.reload() : null}
            />

            <SuccessDialog
                open={openSuccessDialog}
                handleClose={() => {
                    setOpenSuccessDialog(false);
                    onSuccessConfirm();
                }}
                title={successTitle || 'Success'}
                message={successMessage || 'Your action was successful.'}
            />
        </>
    );
};

export default ApiButton;
